import { useState, useEffect, useCallback } from "react";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import {
  CHAT_PANE_DEFAULT_PX,
  CHAT_PANE_MAX_PX,
  CHAT_PANE_MIN_PX,
  getPaneLayout,
} from "src/layouts/paneLayout";

const STORAGE_PREFIX = "tf-ai-chat-pane-width";

export const MIN_WIDTH = CHAT_PANE_MIN_PX;
export const MAX_WIDTH = CHAT_PANE_MAX_PX;

const getStorageKey = (workspaceId) => `${STORAGE_PREFIX}-${workspaceId}`;

const preferredByWorkspace = new Map();
const listenersByWorkspace = new Map();

const clampPreferred = (value) => {
  if (!Number.isFinite(value)) {
    return CHAT_PANE_DEFAULT_PX;
  }
  return Math.min(CHAT_PANE_MAX_PX, Math.max(CHAT_PANE_MIN_PX, value));
};

const readStoredWidth = (id) => {
  try {
    const saved = localStorage.getItem(getStorageKey(id));
    return saved !== null
      ? clampPreferred(parseInt(saved, 10))
      : CHAT_PANE_DEFAULT_PX;
  } catch {
    return CHAT_PANE_DEFAULT_PX;
  }
};

const getPreferred = (id) => {
  if (!preferredByWorkspace.has(id)) {
    preferredByWorkspace.set(id, readStoredWidth(id));
  }
  return preferredByWorkspace.get(id);
};

const setPreferred = (id, next) => {
  const clamped = clampPreferred(next);
  preferredByWorkspace.set(id, clamped);
  try {
    localStorage.setItem(getStorageKey(id), String(clamped));
  } catch {
    // localStorage unavailable
  }
  listenersByWorkspace.get(id)?.forEach((listener) => listener(clamped));
};

/**
 * Persisted preferred width for the consolidated AI chat pane.
 * Displayed width is capped so the middle column stays usable.
 */
const useResizableChatPaneWidth = (isOpen = true) => {
  const workspace = useWorkspace();
  const wsId = workspace?.id || "default";
  const [preferredWidth, setPreferredWidth] = useState(() =>
    getPreferred(wsId)
  );
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window === "undefined" ? 1400 : window.innerWidth
  );

  useEffect(() => {
    setPreferredWidth(getPreferred(wsId));
    if (!listenersByWorkspace.has(wsId)) {
      listenersByWorkspace.set(wsId, new Set());
    }
    const listener = (next) => setPreferredWidth(next);
    listenersByWorkspace.get(wsId).add(listener);
    return () => {
      const listeners = listenersByWorkspace.get(wsId);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          listenersByWorkspace.delete(wsId);
        }
      }
    };
  }, [wsId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setWidth = useCallback(
    (next) => {
      setPreferred(wsId, next);
    },
    [wsId]
  );

  const layout = getPaneLayout({
    windowWidth,
    preferredChatWidth: preferredWidth,
    isChatOpen: isOpen,
  });

  return {
    width: isOpen ? layout.chatWidth : preferredWidth,
    preferredWidth,
    setWidth,
    maxWidth: layout.maxChatWidth,
    smallScreenBreakpoint: layout.smallScreenBreakpoint,
    isSmallScreen: layout.isSmallScreen,
  };
};

export default useResizableChatPaneWidth;
